import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Toast function reference — set by setApiToast() from the app
let showToast = null;

export function setApiToast(toastFn) {
  showToast = toastFn;
}

// Request interceptor — attach XSRF token
api.interceptors.request.use((config) => {
  const token = document.cookie
    .split('; ')
    .find((row) => row.startsWith('XSRF-TOKEN='))
    ?.split('=')[1];
  if (token) {
    config.headers['X-XSRF-TOKEN'] = decodeURIComponent(token);
  }
  return config;
});

// Response interceptor — handle errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const message = error.response?.data?.message;

    if (status === 401) {
      if (!window.location.pathname.startsWith('/login') &&
          !window.location.pathname.startsWith('/register') &&
          !window.location.pathname.startsWith('/forgot-password')) {
        window.location.href = '/login';
      }
    } else if (status === 403) {
      showToast?.error(message || 'You do not have permission to perform this action.');
    } else if (status === 419) {
      showToast?.error('Session expired. Please refresh the page.');
    } else if (status === 429) {
      showToast?.warning('Too many requests. Please wait a moment.');
    } else if (status >= 500) {
      showToast?.error('Server error. Please try again later.');
    }

    return Promise.reject(error);
  }
);

export function getApiUrl(path) {
  return `${API_URL}${path}`;
}

export function getCsrfCookie() {
  return axios.get(`${API_URL}/sanctum/csrf-cookie`, { withCredentials: true });
}

// ═══════════════════════════════════════════════════════════════════════════
// Auth
// ═══════════════════════════════════════════════════════════════════════════

export async function login(email, password, remember = false) {
  await getCsrfCookie();
  const { data } = await api.post('/auth/login', { email, password, remember });
  return data;
}

export async function register({ name, email, password, password_confirmation, company_name, phone }) {
  await getCsrfCookie();
  const { data } = await api.post('/auth/register', {
    name, email, password, password_confirmation, company_name, phone,
  });
  return data;
}

export async function logout() {
  const { data } = await api.post('/auth/logout');
  return data;
}

export async function fetchMe() {
  const { data } = await api.get('/auth/me');
  return data.data;
}

export async function updateProfile(payload) {
  const { data } = await api.put('/auth/me', payload);
  return data;
}

export async function updatePassword(current_password, password, password_confirmation) {
  const { data } = await api.put('/auth/me/password', {
    current_password, password, password_confirmation,
  });
  return data;
}

export async function forgotPassword(email) {
  await getCsrfCookie();
  const { data } = await api.post('/auth/forgot-password', { email });
  return data;
}

export async function resetPassword({ token, email, password, password_confirmation }) {
  await getCsrfCookie();
  const { data } = await api.post('/auth/reset-password', {
    token, email, password, password_confirmation,
  });
  return data;
}

export async function twoFactorChallenge(code, challenge_token) {
  const { data } = await api.post('/auth/2fa/challenge', { code, challenge_token });
  return data;
}

export async function twoFactorSetup() {
  const { data } = await api.post('/auth/2fa/setup');
  return data;
}

export async function twoFactorConfirm(code) {
  const { data } = await api.post('/auth/2fa/confirm', { code });
  return data;
}

export async function twoFactorDisable(password) {
  const { data } = await api.post('/auth/2fa/disable', { password });
  return data;
}

export async function fetchBranding() {
  const { data } = await api.get('/branding');
  return data.data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Dashboard
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchAdminDashboard() {
  const { data } = await api.get('/admin/dashboard');
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Clients
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchClients(params = {}) {
  const { data } = await api.get('/admin/clients', { params });
  return data;
}

export async function fetchClient(id) {
  const { data } = await api.get(`/admin/clients/${id}`);
  return data;
}

export async function createClient(payload) {
  const { data } = await api.post('/admin/clients', payload);
  return data;
}

export async function updateClient(id, payload) {
  const { data } = await api.put(`/admin/clients/${id}`, payload);
  return data;
}

export async function deleteClient(id) {
  const { data } = await api.delete(`/admin/clients/${id}`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Projects
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchProjects(params = {}) {
  const { data } = await api.get('/admin/projects', { params });
  return data;
}

export async function fetchProject(id) {
  const { data } = await api.get(`/admin/projects/${id}`);
  return data;
}

export async function createProject(payload) {
  const { data } = await api.post('/admin/projects', payload);
  return data;
}

export async function updateProject(id, payload) {
  const { data } = await api.put(`/admin/projects/${id}`, payload);
  return data;
}

export async function deleteProject(id) {
  const { data } = await api.delete(`/admin/projects/${id}`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Environments
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchEnvironments(projectId, params = {}) {
  const { data } = await api.get(`/admin/projects/${projectId}/environments`, { params });
  return data;
}

export async function fetchEnvironment(id) {
  const { data } = await api.get(`/admin/environments/${id}`);
  return data;
}

export async function createEnvironment(projectId, payload) {
  const { data } = await api.post(`/admin/projects/${projectId}/environments`, payload);
  return data;
}

export async function updateEnvironment(id, payload) {
  const { data } = await api.put(`/admin/environments/${id}`, payload);
  return data;
}

export async function deleteEnvironment(id) {
  const { data } = await api.delete(`/admin/environments/${id}`);
  return data;
}

export async function fetchEnvironmentHealth(id) {
  const { data } = await api.get(`/admin/environments/${id}/health`);
  return data;
}

export async function verifySsh(id) {
  const { data } = await api.post(`/admin/environments/${id}/verify-ssh`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Maintenance
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchMaintenanceTasks(params = {}) {
  const { data } = await api.get('/admin/maintenance', { params });
  return data;
}

export async function runMaintenance(payload) {
  const { data } = await api.post('/admin/maintenance/run', payload);
  return data;
}

export async function fetchMaintenanceRun(id) {
  const { data } = await api.get(`/admin/maintenance/runs/${id}`);
  return data;
}

export async function bulkMaintenance(payload) {
  const { data } = await api.post('/admin/maintenance/bulk', payload);
  return data;
}

export async function fetchMaintenanceHistory(params = {}) {
  const { data } = await api.get('/admin/maintenance/history', { params });
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Tickets
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchTickets(params = {}) {
  const { data } = await api.get('/admin/tickets', { params });
  return data;
}

export async function fetchTicket(id) {
  const { data } = await api.get(`/admin/tickets/${id}`);
  return data;
}

export async function createTicket(payload) {
  const { data } = await api.post('/admin/tickets', payload);
  return data;
}

export async function updateTicket(id, payload) {
  const { data } = await api.put(`/admin/tickets/${id}`, payload);
  return data;
}

export async function replyToTicket(id, payload) {
  const { data } = await api.post(`/admin/tickets/${id}/reply`, payload);
  return data;
}

export async function assignTicket(id, payload) {
  const { data } = await api.put(`/admin/tickets/${id}/assign`, payload);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Billing / Invoices
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchBillingDashboard() {
  const { data } = await api.get('/admin/billing/dashboard');
  return data;
}

export async function fetchInvoices(params = {}) {
  const { data } = await api.get('/admin/invoices', { params });
  return data;
}

export async function fetchInvoice(id) {
  const { data } = await api.get(`/admin/invoices/${id}`);
  return data;
}

export async function createInvoice(payload) {
  const { data } = await api.post('/admin/invoices', payload);
  return data;
}

export async function updateInvoice(id, payload) {
  const { data } = await api.put(`/admin/invoices/${id}`, payload);
  return data;
}

export async function recordPayment(invoiceId, payload) {
  const { data } = await api.post(`/admin/invoices/${invoiceId}/payment`, payload);
  return data;
}

export function getInvoicePdfUrl(id) {
  return getApiUrl(`/admin/invoices/${id}/pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Reports
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchReports(params = {}) {
  const { data } = await api.get('/admin/reports', { params });
  return data;
}

export async function fetchReport(id) {
  const { data } = await api.get(`/admin/reports/${id}`);
  return data;
}

export async function generateReport(payload) {
  const { data } = await api.post('/admin/reports/generate', payload);
  return data;
}

export function getReportPdfUrl(id) {
  return getApiUrl(`/admin/reports/${id}/pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Health
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchHealthOverview() {
  const { data } = await api.get('/admin/health');
  return data;
}

export async function fetchEnvironmentHealthDetail(envId) {
  const { data } = await api.get(`/admin/health/environments/${envId}`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Users & Roles (super_admin)
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchUsers(params = {}) {
  const { data } = await api.get('/admin/users', { params });
  return data;
}

export async function fetchUser(id) {
  const { data } = await api.get(`/admin/users/${id}`);
  return data;
}

export async function createUser(payload) {
  const { data } = await api.post('/admin/users', payload);
  return data;
}

export async function updateUser(id, payload) {
  const { data } = await api.put(`/admin/users/${id}`, payload);
  return data;
}

export async function deleteUser(id) {
  const { data } = await api.delete(`/admin/users/${id}`);
  return data;
}

export async function fetchRoles(params = {}) {
  const { data } = await api.get('/admin/roles', { params });
  return data;
}

export async function assignRole(payload) {
  const { data } = await api.post('/admin/roles/assign', payload);
  return data;
}

export async function removeRole(payload) {
  const { data } = await api.post('/admin/roles/remove', payload);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Settings & Audit
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchSettings() {
  const { data } = await api.get('/admin/settings');
  return data;
}

export async function updateSettings(payload) {
  const { data } = await api.put('/admin/settings', payload);
  return data;
}

export async function fetchAuditTrail(params = {}) {
  const { data } = await api.get('/admin/audit-trail', { params });
  return data;
}

export async function fetchActivityLog(params = {}) {
  const { data } = await api.get('/admin/activity-log', { params });
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — SSL Certificates
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchSslCertificates(params = {}) {
  const { data } = await api.get('/admin/ssl', { params });
  return data;
}

export async function checkSslCertificate(certificateId) {
  const { data } = await api.post(`/admin/ssl/${certificateId}/check`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — SLA Policies
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchSlaPolicies(params = {}) {
  const { data } = await api.get('/admin/sla-policies', { params });
  return data;
}

export async function fetchSlaPolicy(id) {
  const { data } = await api.get(`/admin/sla-policies/${id}`);
  return data;
}

export async function createSlaPolicy(payload) {
  const { data } = await api.post('/admin/sla-policies', payload);
  return data;
}

export async function updateSlaPolicy(id, payload) {
  const { data } = await api.put(`/admin/sla-policies/${id}`, payload);
  return data;
}

export async function deleteSlaPolicy(id) {
  const { data } = await api.delete(`/admin/sla-policies/${id}`);
  return data;
}

export async function fetchSlaBreaches(params = {}) {
  const { data } = await api.get('/admin/sla/breaches', { params });
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Runbooks
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchRunbooks(params = {}) {
  const { data } = await api.get('/admin/runbooks', { params });
  return data;
}

export async function fetchRunbook(id) {
  const { data } = await api.get(`/admin/runbooks/${id}`);
  return data;
}

export async function createRunbook(payload) {
  const { data } = await api.post('/admin/runbooks', payload);
  return data;
}

export async function updateRunbook(id, payload) {
  const { data } = await api.put(`/admin/runbooks/${id}`, payload);
  return data;
}

export async function deleteRunbook(id) {
  const { data } = await api.delete(`/admin/runbooks/${id}`);
  return data;
}

export async function triggerRunbook(id) {
  const { data } = await api.post(`/admin/runbooks/${id}/trigger`);
  return data;
}

export async function fetchRunbookExecutions(id, params = {}) {
  const { data } = await api.get(`/admin/runbooks/${id}/executions`, { params });
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Custom Tasks
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchCustomTasks(params = {}) {
  const { data } = await api.get('/admin/custom-tasks', { params });
  return data;
}

export async function fetchCustomTask(id) {
  const { data } = await api.get(`/admin/custom-tasks/${id}`);
  return data;
}

export async function createCustomTask(payload) {
  const { data } = await api.post('/admin/custom-tasks', payload);
  return data;
}

export async function updateCustomTask(id, payload) {
  const { data } = await api.put(`/admin/custom-tasks/${id}`, payload);
  return data;
}

export async function deleteCustomTask(id) {
  const { data } = await api.delete(`/admin/custom-tasks/${id}`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Notification Rules
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchNotificationRules(params = {}) {
  const { data } = await api.get('/admin/notification-rules', { params });
  return data;
}

export async function fetchNotificationRule(id) {
  const { data } = await api.get(`/admin/notification-rules/${id}`);
  return data;
}

export async function createNotificationRule(payload) {
  const { data } = await api.post('/admin/notification-rules', payload);
  return data;
}

export async function updateNotificationRule(id, payload) {
  const { data } = await api.put(`/admin/notification-rules/${id}`, payload);
  return data;
}

export async function deleteNotificationRule(id) {
  const { data } = await api.delete(`/admin/notification-rules/${id}`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Integrations
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchIntegrations(params = {}) {
  const { data } = await api.get('/admin/integrations', { params });
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Canned Responses
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchCannedResponses(params = {}) {
  const { data } = await api.get('/admin/canned-responses', { params });
  return data;
}

export async function fetchCannedResponse(id) {
  const { data } = await api.get(`/admin/canned-responses/${id}`);
  return data;
}

export async function createCannedResponse(payload) {
  const { data } = await api.post('/admin/canned-responses', payload);
  return data;
}

export async function updateCannedResponse(id, payload) {
  const { data } = await api.put(`/admin/canned-responses/${id}`, payload);
  return data;
}

export async function deleteCannedResponse(id) {
  const { data } = await api.delete(`/admin/canned-responses/${id}`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Knowledge Base
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchAdminKbArticles(params = {}) {
  const { data } = await api.get('/admin/kb', { params });
  return data;
}

export async function fetchAdminKbArticle(id) {
  const { data } = await api.get(`/admin/kb/${id}`);
  return data;
}

export async function createKbArticle(payload) {
  const { data } = await api.post('/admin/kb', payload);
  return data;
}

export async function updateKbArticle(id, payload) {
  const { data } = await api.put(`/admin/kb/${id}`, payload);
  return data;
}

export async function deleteKbArticle(id) {
  const { data } = await api.delete(`/admin/kb/${id}`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Blog
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchAdminBlogPosts(params = {}) {
  const { data } = await api.get('/admin/blog', { params });
  return data;
}

export async function fetchAdminBlogPost(id) {
  const { data } = await api.get(`/admin/blog/${id}`);
  return data;
}

export async function createBlogPost(payload) {
  const { data } = await api.post('/admin/blog', payload);
  return data;
}

export async function updateBlogPost(id, payload) {
  const { data } = await api.put(`/admin/blog/${id}`, payload);
  return data;
}

export async function deleteBlogPost(id) {
  const { data } = await api.delete(`/admin/blog/${id}`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Changelog
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchAdminChangelogs(params = {}) {
  const { data } = await api.get('/admin/changelog', { params });
  return data;
}

export async function fetchAdminChangelog(id) {
  const { data } = await api.get(`/admin/changelog/${id}`);
  return data;
}

export async function createChangelog(payload) {
  const { data } = await api.post('/admin/changelog', payload);
  return data;
}

export async function updateChangelog(id, payload) {
  const { data } = await api.put(`/admin/changelog/${id}`, payload);
  return data;
}

export async function deleteChangelog(id) {
  const { data } = await api.delete(`/admin/changelog/${id}`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Service Requests
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchServiceRequests(params = {}) {
  const { data } = await api.get('/admin/service-requests', { params });
  return data;
}

export async function fetchServiceRequest(id) {
  const { data } = await api.get(`/admin/service-requests/${id}`);
  return data;
}

export async function createServiceRequest(payload) {
  const { data } = await api.post('/admin/service-requests', payload);
  return data;
}

export async function updateServiceRequest(id, payload) {
  const { data } = await api.put(`/admin/service-requests/${id}`, payload);
  return data;
}

export async function deleteServiceRequest(id) {
  const { data } = await api.delete(`/admin/service-requests/${id}`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Backups
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchBackups(params = {}) {
  const { data } = await api.get('/admin/backups', { params });
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin — Global Search
// ═══════════════════════════════════════════════════════════════════════════

export async function globalSearch(query) {
  const { data } = await api.get('/admin/search', { params: { q: query } });
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Client — Dashboard
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchClientDashboard() {
  const { data } = await api.get('/client/dashboard');
  return data.data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Client — Projects
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchMyProjects(params = {}) {
  const { data } = await api.get('/client/projects', { params });
  return data;
}

export async function fetchMyProject(id) {
  const { data } = await api.get(`/client/projects/${id}`);
  return data;
}

export async function fetchProjectEnvironments(projectId) {
  const { data } = await api.get(`/client/projects/${projectId}/environments`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Client — Tickets
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchMyTickets(params = {}) {
  const { data } = await api.get('/client/tickets', { params });
  return data;
}

export async function fetchMyTicket(id) {
  const { data } = await api.get(`/client/tickets/${id}`);
  return data;
}

export async function createMyTicket(payload) {
  const { data } = await api.post('/client/tickets', payload);
  return data;
}

export async function replyToMyTicket(id, payload) {
  const { data } = await api.post(`/client/tickets/${id}/reply`, payload);
  return data;
}

export async function updateMyTicket(id, payload) {
  const { data } = await api.put(`/client/tickets/${id}`, payload);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Client — Billing
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchMyBilling() {
  const { data } = await api.get('/client/billing');
  return data.data;
}

export async function fetchMyInvoices(params = {}) {
  const { data } = await api.get('/client/invoices', { params });
  return data;
}

export async function fetchMyInvoice(id) {
  const { data } = await api.get(`/client/invoices/${id}`);
  return data;
}

export function getMyInvoicePdfUrl(id) {
  return getApiUrl(`/client/invoices/${id}/pdf`);
}

export async function fetchMySubscription() {
  const { data } = await api.get('/client/subscription');
  return data.data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Client — Reports
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchMyReports(params = {}) {
  const { data } = await api.get('/client/reports', { params });
  return data;
}

export async function fetchMyReport(id) {
  const { data } = await api.get(`/client/reports/${id}`);
  return data;
}

export function getMyReportPdfUrl(id) {
  return getApiUrl(`/client/reports/${id}/pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════
// Client — Maintenance
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchMyMaintenanceTasks() {
  const { data } = await api.get('/client/maintenance/tasks');
  return data.data;
}

export async function runMyMaintenance(payload) {
  const { data } = await api.post('/client/maintenance/run', payload);
  return data;
}

export async function fetchMyMaintenanceHistory(params = {}) {
  const { data } = await api.get('/client/maintenance/history', { params });
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Client — Help Center
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchKbArticles(params = {}) {
  const { data } = await api.get('/client/help/kb', { params });
  return data;
}

export async function fetchKbArticle(slug) {
  const { data } = await api.get(`/client/help/kb/${slug}`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Client — Service Requests
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchMyServiceRequests(params = {}) {
  const { data } = await api.get('/client/service-requests', { params });
  return data;
}

export async function fetchMyServiceRequest(id) {
  const { data } = await api.get(`/client/service-requests/${id}`);
  return data;
}

export async function createMyServiceRequest(payload) {
  const { data } = await api.post('/client/service-requests', payload);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Client — Team Management
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchTeamMembers(params = {}) {
  const { data } = await api.get('/client/team', { params });
  return data;
}

export async function fetchTeamMember(id) {
  const { data } = await api.get(`/client/team/${id}`);
  return data;
}

export async function addTeamMember(payload) {
  const { data } = await api.post('/client/team', payload);
  return data;
}

export async function updateTeamMember(id, payload) {
  const { data } = await api.put(`/client/team/${id}`, payload);
  return data;
}

export async function removeTeamMember(id) {
  const { data } = await api.delete(`/client/team/${id}`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Client — API Tokens
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchApiTokens(params = {}) {
  const { data } = await api.get('/client/api-tokens', { params });
  return data;
}

export async function createApiToken(payload) {
  const { data } = await api.post('/client/api-tokens', payload);
  return data;
}

export async function deleteApiToken(id) {
  const { data } = await api.delete(`/client/api-tokens/${id}`);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Client — Notifications
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchNotifications(params = {}) {
  const { data } = await api.get('/client/notifications', { params });
  return data;
}

export async function updateNotificationPreferences(payload) {
  const { data } = await api.put('/client/notifications/preferences', payload);
  return data;
}

export async function updateWebhookUrl(payload) {
  const { data } = await api.put('/client/webhooks', payload);
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Client — Onboarding
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchOnboardingStatus() {
  const { data } = await api.get('/client/onboarding');
  return data.data;
}

export async function completeOnboarding() {
  const { data } = await api.post('/client/onboarding/complete');
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Public
// ═══════════════════════════════════════════════════════════════════════════

export async function fetchBlogPosts(params = {}) {
  const { data } = await api.get('/public/blog', { params });
  return data;
}

export async function fetchBlogPost(slug) {
  const { data } = await api.get(`/public/blog/${slug}`);
  return data;
}

export async function fetchPublicChangelog(params = {}) {
  const { data } = await api.get('/public/changelog', { params });
  return data;
}

export async function fetchPublicKbArticles(params = {}) {
  const { data } = await api.get('/public/kb', { params });
  return data;
}

export async function fetchPublicKbArticle(slug) {
  const { data } = await api.get(`/public/kb/${slug}`);
  return data;
}

export async function submitContactForm(payload) {
  const { data } = await api.post('/public/contact', payload);
  return data;
}

export async function fetchStatusPage(token) {
  const { data } = await api.get(`/public/status/${token}`);
  return data;
}

export async function fetchPlans() {
  const { data } = await api.get('/public/plans');
  return data;
}

export default api;
